import { useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import {
  ChevronLeft,
  ChevronRight,
  XClose,
  SearchLg,
  CalendarDate,
  Settings01,
} from "@untitledui/icons"
import { toast } from "sonner"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import { useOrganization } from "@/lib/organization/organization-context"
import {
  useAppointments,
  useSlots,
  useCreateAppointment,
  useRescheduleAppointment,
  useCancelAppointment,
  useSetAppointmentOutcome,
} from "@/lib/api/use-appointments"
import type { AppointmentKind, ShopAppointment, SlotInfo } from "@/lib/api/appointments"
import { useEstimates } from "@/lib/api/use-estimates"
import { estimateCustomerName, vehicleDisplayName } from "@/lib/api/types"
import type { ApiEstimate } from "@/lib/api/types"
import { formatMoney } from "@/lib/service-lane/baseline-math"
import { cx } from "@/lib/utils/cx"

// The shop's calendar — bookings and free space on one grid, driven by the
// same slot math the customer's booking link uses (they can never disagree).
// Free slots book a customer in two taps; bookings reschedule or cancel and
// the estimate follows (Scheduled ⇄ InProgress) everywhere else in the app.

const KIND_LABEL: Record<AppointmentKind, string> = {
  inspection: "Inspection",
  dropoff: "Drop-off",
}

function startOfWeek(offsetWeeks: number): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  const diffToMonday = (day + 6) % 7
  d.setDate(d.getDate() - diffToMonday + offsetWeeks * 7)
  return d
}

function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
}

export default function CalendarPage() {
  const { activeTeamId, activeTeamKind } = useOrganization()
  const [params] = useSearchParams()
  // Deep-links land on the right week: /calendar?d=2026-07-24 opens the week
  // containing that day (the card's "Booked · Thu" chip, the estimate's
  // appointment block).
  const [weekOffset, setWeekOffset] = useState(() => {
    const d = params.get("d")
    if (!d) return 0
    const target = new Date(`${d}T12:00:00`)
    if (Number.isNaN(target.getTime())) return 0
    const thisMonday = startOfWeek(0)
    const targetMonday = new Date(target)
    targetMonday.setHours(0, 0, 0, 0)
    targetMonday.setDate(targetMonday.getDate() - ((targetMonday.getDay() + 6) % 7))
    return Math.round((targetMonday.getTime() - thisMonday.getTime()) / (7 * 86_400_000))
  })
  const [bookSlot, setBookSlot] = useState<string | null>(null)
  const [detail, setDetail] = useState<ShopAppointment | null>(null)
  const [mobileDay, setMobileDay] = useState<string | null>(null)

  const from = useMemo(() => startOfWeek(weekOffset).toISOString(), [weekOffset])
  const to = useMemo(() => {
    const d = startOfWeek(weekOffset)
    d.setDate(d.getDate() + 6)
    d.setHours(23, 59, 59, 999)
    return d.toISOString()
  }, [weekOffset])

  const { data: appts = [], isLoading: apptsLoading } = useAppointments(activeTeamId, from, to)
  const { data: slots = [], isLoading: slotsLoading } = useSlots(activeTeamId, from, to)
  const isLoading = apptsLoading || slotsLoading

  // Group the week: open days come straight from the slot list (closed and
  // blocked days simply have no slots), rows are the union of slot times.
  const { days, times, slotByIso, apptsByIso } = useMemo(() => {
    const dayMap = new Map<string, { date: Date; slots: SlotInfo[] }>()
    for (const s of slots) {
      const k = dayKey(s.startsAt)
      const entry = dayMap.get(k) ?? { date: new Date(s.startsAt), slots: [] }
      entry.slots.push(s)
      dayMap.set(k, entry)
    }
    const days = [...dayMap.values()].sort((a, b) => a.date.getTime() - b.date.getTime())
    const timeSet = new Map<string, number>()
    for (const s of slots) {
      const d = new Date(s.startsAt)
      const label = timeLabel(s.startsAt)
      timeSet.set(label, d.getHours() * 60 + d.getMinutes())
    }
    const times = [...timeSet.entries()].sort((a, b) => a[1] - b[1]).map(([l]) => l)
    const slotByIso = new Map(slots.map((s) => [s.startsAt, s]))
    const apptsByIso = new Map<string, ShopAppointment[]>()
    for (const a of appts) {
      const arr = apptsByIso.get(a.startsAt) ?? []
      arr.push(a)
      apptsByIso.set(a.startsAt, arr)
    }
    return { days, times, slotByIso, apptsByIso }
  }, [slots, appts])

  const weekValue = appts.reduce((s, a) => s + (a.estimateValue || 0), 0)
  const todayKey = dayKey(new Date().toISOString())
  // Passed visits nobody checked in — each needs a "did they show?" answer
  // so the estimate either completes its journey or goes back to follow-up.
  const needOutcome = appts.filter(
    (a) => a.outcome == null && new Date(a.startsAt).getTime() < Date.now() - 30 * 60_000,
  )
  const rangeLabel = useMemo(() => {
    if (days.length === 0) return ""
    const f = days[0].date
    const l = days[days.length - 1].date
    const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    return `${fmt(f)} – ${fmt(l)}`
  }, [days])

  const activeMobileDay = mobileDay ?? days.find((d) => dayKey(d.date.toISOString()) === todayKey)
    ? (mobileDay ?? todayKey)
    : days[0]
      ? dayKey(days[0].date.toISOString())
      : null

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          {/* Wider canvas + taller rows — the grid reads at arm's length. */}
          <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 md:py-10">
            <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
                  Calendar
                </h1>
                <p className="text-sm text-zinc-500 mt-0.5">
                  {appts.length > 0 ? (
                    <>
                      This week:{" "}
                      <span className="font-semibold text-zinc-700">
                        {appts.length} car{appts.length === 1 ? "" : "s"} coming in
                      </span>
                      {weekValue > 0 && (
                        <>
                          {" · "}
                          <span className="font-semibold text-emerald-700 tabular-nums">
                            {formatMoney(weekValue)}
                          </span>{" "}
                          booked
                        </>
                      )}
                    </>
                  ) : (
                    "Bookings and free space — customers book themselves from the follow-up link."
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {activeTeamKind === "bodyshop" && activeTeamId != null && (
                  <Link
                    to={`/settings/bodyshop/${activeTeamId}/booking`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-zinc-200 bg-white text-[12.5px] font-semibold text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-colors"
                  >
                    <Settings01 className="size-3.5" aria-hidden />
                    Working hours
                  </Link>
                )}
                <div className="flex items-center rounded-xl border border-zinc-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setWeekOffset((w) => w - 1)}
                    aria-label="Previous week"
                    className="size-9 grid place-items-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                  >
                    <ChevronLeft className="size-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeekOffset(0)}
                    className={cx(
                      "h-9 px-3 text-[12.5px] font-semibold border-x border-zinc-200",
                      weekOffset === 0
                        ? "text-zinc-900"
                        : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50",
                    )}
                  >
                    {weekOffset === 0 ? "This week" : rangeLabel || "Today"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeekOffset((w) => w + 1)}
                    aria-label="Next week"
                    className="size-9 grid place-items-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                  >
                    <ChevronRight className="size-4" aria-hidden />
                  </button>
                </div>
              </div>
            </header>

            {/* Did-they-show — visits that passed without a check-in. */}
            {needOutcome.length > 0 && (
              <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <p className="text-[13px] font-semibold text-amber-900">
                    Did they show?
                  </p>
                  {needOutcome.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setDetail(a)}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-white border border-amber-200 text-[12px] font-semibold text-zinc-700 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                    >
                      {a.customerName}
                      <span className="text-[10.5px] font-medium text-zinc-400 tabular-nums">
                        {new Date(a.startsAt).toLocaleString("en-US", {
                          weekday: "short",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="h-[480px] rounded-2xl bg-white border border-zinc-200/70 animate-pulse" />
            ) : days.length === 0 ? (
              <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
                <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
                  <CalendarDate className="size-5 text-zinc-400" aria-hidden />
                </div>
                <p className="text-[14px] font-semibold text-zinc-900">No open hours this week</p>
                <p className="text-[13px] text-zinc-500 mt-1">
                  Adjust working hours in settings, or move to another week.
                </p>
              </div>
            ) : (
              <>
                {/* Desktop — the week grid */}
                <div className="hidden md:block rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-100 bg-zinc-50/60">
                          <th className="w-[84px] px-2 py-3" />
                          {days.map((d) => {
                            const isToday = dayKey(d.date.toISOString()) === todayKey
                            return (
                              <th
                                key={d.date.toISOString()}
                                className={cx(
                                  "px-2 py-3 text-center text-[12.5px] font-bold uppercase tracking-[0.06em]",
                                  isToday ? "text-emerald-700" : "text-zinc-500",
                                )}
                              >
                                {d.date.toLocaleDateString("en-US", { weekday: "short" })}{" "}
                                <span className={cx("tabular-nums", isToday ? "" : "text-zinc-400")}>
                                  {d.date.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                                {isToday && (
                                  <span className="ml-1 inline-block size-1.5 rounded-full bg-emerald-500 align-middle" aria-hidden />
                                )}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {times.map((t) => (
                          <tr key={t} className="border-b border-zinc-100 last:border-b-0">
                            <td className="px-2.5 py-1.5 text-right text-[11.5px] text-zinc-400 tabular-nums align-top pt-3 whitespace-nowrap">
                              {t}
                            </td>
                            {days.map((d) => {
                              const slot = d.slots.find((s) => timeLabel(s.startsAt) === t)
                              if (!slot) {
                                return (
                                  <td
                                    key={d.date.toISOString() + t}
                                    className="border-l border-zinc-100 bg-zinc-50/40"
                                  />
                                )
                              }
                              const cellAppts = apptsByIso.get(slot.startsAt) ?? []
                              const bookable = slot.free > 0
                              return (
                                <td
                                  key={slot.startsAt}
                                  className="border-l border-zinc-100 p-1.5 align-top min-w-[150px]"
                                >
                                  <div className="space-y-1.5 min-h-[56px]">
                                    {cellAppts.map((a) => (
                                      <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => setDetail(a)}
                                        className={cx(
                                          "w-full text-left rounded-lg px-2.5 py-2 text-[12.5px] font-semibold leading-tight truncate transition-colors",
                                          a.outcome === "no_show"
                                            ? "bg-zinc-50 text-zinc-400 ring-1 ring-inset ring-zinc-200 line-through decoration-zinc-300 hover:bg-zinc-100"
                                            : a.outcome == null &&
                                                new Date(a.startsAt).getTime() < Date.now() - 30 * 60_000
                                              ? "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 hover:bg-amber-100"
                                              : a.estimateId != null
                                                ? "bg-teal-50 text-teal-800 ring-1 ring-inset ring-teal-200 hover:bg-teal-100"
                                                : "bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-200",
                                        )}
                                        title={`${a.customerName} · ${KIND_LABEL[a.kind]}`}
                                      >
                                        {a.customerName}
                                        <span className="block text-[11px] font-medium opacity-70 truncate mt-0.5">
                                          {KIND_LABEL[a.kind]}
                                          {a.estimateValue ? ` · ${formatMoney(a.estimateValue)}` : ""}
                                        </span>
                                      </button>
                                    ))}
                                    {/* Free space stays quiet: an empty slot is one
                                        big soft target ("+ Book" on hover), a partial
                                        slot a small "+N" chip — not a wall of dashed
                                        "2 free" buttons. */}
                                    {bookable && cellAppts.length === 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => setBookSlot(slot.startsAt)}
                                        aria-label={`Book ${t}`}
                                        className="group w-full min-h-[52px] rounded-lg text-[13px] font-semibold text-zinc-300 hover:text-emerald-700 hover:bg-emerald-50/60 transition-colors"
                                      >
                                        +<span className="hidden group-hover:inline"> Book</span>
                                      </button>
                                    ) : bookable ? (
                                      <button
                                        type="button"
                                        onClick={() => setBookSlot(slot.startsAt)}
                                        className="w-full rounded-lg border border-dashed border-zinc-200 px-2 py-1 text-[11px] font-semibold text-zinc-400 hover:text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
                                      >
                                        + {slot.free} more
                                      </button>
                                    ) : cellAppts.length > 0 ? (
                                      <p className="text-center text-[10px] font-bold uppercase tracking-wide text-zinc-300">
                                        Full
                                      </p>
                                    ) : null}
                                  </div>
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile — day pills + that day's slots */}
                <div className="md:hidden space-y-3">
                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {days.map((d) => {
                      const k = dayKey(d.date.toISOString())
                      const active = k === activeMobileDay
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setMobileDay(k)}
                          className={cx(
                            "shrink-0 h-9 px-3 rounded-lg text-[12px] font-semibold",
                            active
                              ? "bg-zinc-900 text-white"
                              : "bg-white border border-zinc-200 text-zinc-600",
                          )}
                        >
                          {d.date.toLocaleDateString("en-US", { weekday: "short", day: "numeric" })}
                        </button>
                      )
                    })}
                  </div>
                  <div className="rounded-2xl border border-zinc-200/80 bg-white divide-y divide-zinc-100 overflow-hidden">
                    {(days.find((d) => dayKey(d.date.toISOString()) === activeMobileDay)?.slots ?? []).map(
                      (slot) => {
                        const cellAppts = apptsByIso.get(slot.startsAt) ?? []
                        return (
                          <div key={slot.startsAt} className="flex items-start gap-3 px-3.5 py-2.5">
                            <span className="w-16 shrink-0 text-[11.5px] text-zinc-400 tabular-nums pt-1">
                              {timeLabel(slot.startsAt)}
                            </span>
                            <div className="min-w-0 flex-1 space-y-1">
                              {cellAppts.map((a) => (
                                <button
                                  key={a.id}
                                  type="button"
                                  onClick={() => setDetail(a)}
                                  className={cx(
                                    "w-full text-left rounded-lg px-3 py-2 text-[13px] font-semibold",
                                    a.outcome === "no_show"
                                      ? "bg-zinc-50 text-zinc-400 ring-1 ring-inset ring-zinc-200 line-through decoration-zinc-300"
                                      : a.outcome == null &&
                                          new Date(a.startsAt).getTime() < Date.now() - 30 * 60_000
                                        ? "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200"
                                        : a.estimateId != null
                                          ? "bg-teal-50 text-teal-800 ring-1 ring-inset ring-teal-200"
                                          : "bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200",
                                  )}
                                >
                                  {a.customerName}
                                  <span className="block text-[11px] font-medium opacity-70 mt-0.5">
                                    {KIND_LABEL[a.kind]}
                                    {a.estimateValue ? ` · ${formatMoney(a.estimateValue)}` : ""}
                                  </span>
                                </button>
                              ))}
                              {slot.free > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setBookSlot(slot.startsAt)}
                                  className={cx(
                                    "w-full rounded-lg px-3 text-left text-[12.5px] font-semibold transition-colors",
                                    cellAppts.length === 0
                                      ? "py-2.5 text-zinc-300 active:bg-emerald-50"
                                      : "py-1.5 border border-dashed border-zinc-200 text-zinc-400",
                                  )}
                                >
                                  {cellAppts.length === 0 ? "+ Book" : `+ ${slot.free} more`}
                                </button>
                              ) : cellAppts.length > 0 ? (
                                <p className="text-[10px] font-bold uppercase text-zinc-300 px-1">
                                  Full
                                </p>
                              ) : null}
                            </div>
                          </div>
                        )
                      },
                    )}
                  </div>
                </div>
              </>
            )}

            {bookSlot && activeTeamId != null && (
              <BookDialog
                teamId={Number(activeTeamId)}
                startsAt={bookSlot}
                onClose={() => setBookSlot(null)}
              />
            )}
            {detail && (
              <AppointmentDialog
                appt={detail}
                freeSlots={slots.filter((s) => s.free > 0)}
                onClose={() => setDetail(null)}
              />
            )}
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

/* ── Book a free slot — an estimate, or a walk-in ─────────── */

function BookDialog({
  teamId,
  startsAt,
  onClose,
}: {
  teamId: number
  startsAt: string
  onClose: () => void
}) {
  const create = useCreateAppointment()
  const { data: estimates = [] } = useEstimates(teamId)
  const [kind, setKind] = useState<AppointmentKind>("dropoff")
  const [mode, setMode] = useState<"estimate" | "walkin">("estimate")
  const [q, setQ] = useState("")
  // Select first, book on the explicit button — one stray tap must never
  // create a real booking.
  const [selected, setSelected] = useState<ApiEstimate | null>(null)
  const [walkName, setWalkName] = useState("")
  const [walkPhone, setWalkPhone] = useState("")
  const [walkVehicle, setWalkVehicle] = useState("")

  const candidates = useMemo(
    () =>
      estimates
        .filter(
          (e) =>
            e.isActive !== false &&
            (e.status === "Pending" || e.status === "Confirmed" || e.status === "InProgress"),
        )
        .filter((e) => {
          const term = q.trim().toLowerCase()
          if (!term) return true
          return (
            estimateCustomerName(e).toLowerCase().includes(term) ||
            vehicleDisplayName(e.vehicle).toLowerCase().includes(term)
          )
        })
        .slice(0, 6),
    [estimates, q],
  )

  const when = new Date(startsAt).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

  const book = (estimate: ApiEstimate | null) => {
    if (create.isPending) return
    if (!estimate && !walkName.trim()) return
    create.mutate(
      {
        teamId,
        estimateId: estimate?.id ?? null,
        kind,
        startsAt,
        customerName: estimate ? undefined : walkName.trim(),
        customerPhone: estimate ? undefined : walkPhone.trim() || undefined,
        vehicleLabel: estimate ? undefined : walkVehicle.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            estimate
              ? `${estimateCustomerName(estimate)} booked — ${when}`
              : `${walkName.trim()} booked — ${when}`,
          )
          onClose()
        },
        onError: () => toast.error("Couldn't book that slot."),
      },
    )
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-zinc-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Book this slot"
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[16px] font-bold text-zinc-900">Book this slot</p>
            <p className="text-[12.5px] text-zinc-500 mt-0.5">{when}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 shrink-0"
          >
            <XClose className="size-4" aria-hidden />
          </button>
        </div>

        <div className="mt-3 flex gap-1.5">
          {(["dropoff", "inspection"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={cx(
                "h-8 px-3 rounded-lg text-[12px] font-semibold",
                kind === k
                  ? "bg-zinc-900 text-white"
                  : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300",
              )}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
          <span className="flex-1" />
          {(["estimate", "walkin"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cx(
                "h-8 px-3 rounded-lg text-[12px] font-semibold",
                mode === m
                  ? "bg-teal-600 text-white"
                  : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300",
              )}
            >
              {m === "estimate" ? "From an estimate" : "Walk-in"}
            </button>
          ))}
        </div>

        {mode === "estimate" ? (
          <div className="mt-3">
            <div className="relative">
              <SearchLg
                className="size-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2"
                aria-hidden
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search estimates"
                placeholder="Search customer or vehicle…"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-zinc-200 bg-white text-[13.5px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
            </div>
            <div className="mt-2 divide-y divide-zinc-100 rounded-xl border border-zinc-200 overflow-hidden max-h-64 overflow-y-auto">
              {candidates.length === 0 && (
                <p className="px-3 py-5 text-center text-[12.5px] text-zinc-400">
                  No open estimates match.
                </p>
              )}
              {candidates.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelected(selected?.id === e.id ? null : e)}
                  className={cx(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                    selected?.id === e.id ? "bg-teal-50/70" : "hover:bg-zinc-50",
                  )}
                >
                  <span
                    className={cx(
                      "size-2 rounded-full shrink-0",
                      selected?.id === e.id ? "bg-teal-500" : "bg-zinc-200",
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-zinc-900 truncate">
                      {estimateCustomerName(e)}
                    </p>
                    <p className="text-[11.5px] text-zinc-400 truncate">
                      {vehicleDisplayName(e.vehicle)} · {e.status}
                    </p>
                  </div>
                  <span className="text-[13px] font-bold text-zinc-800 tabular-nums shrink-0">
                    {formatMoney(e.avg)}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!selected || create.isPending}
              onClick={() => selected && book(selected)}
              className="mt-3 w-full h-10 rounded-xl bg-teal-600 text-white text-[13px] font-semibold hover:bg-teal-700 transition-colors disabled:opacity-40"
            >
              {create.isPending
                ? "Booking…"
                : selected
                  ? `Book ${estimateCustomerName(selected).split(" ")[0]}`
                  : "Pick an estimate above"}
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            <input
              value={walkName}
              onChange={(e) => setWalkName(e.target.value)}
              aria-label="Customer name"
              placeholder="Customer name"
              className="w-full h-10 px-3 rounded-xl border border-zinc-200 text-[13.5px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
            />
            <div className="flex gap-2">
              <input
                value={walkPhone}
                onChange={(e) => setWalkPhone(e.target.value)}
                aria-label="Phone"
                placeholder="Phone (optional)"
                className="flex-1 h-10 px-3 rounded-xl border border-zinc-200 text-[13.5px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
              <input
                value={walkVehicle}
                onChange={(e) => setWalkVehicle(e.target.value)}
                aria-label="Vehicle"
                placeholder="Vehicle (optional)"
                className="flex-1 h-10 px-3 rounded-xl border border-zinc-200 text-[13.5px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
            </div>
            <button
              type="button"
              onClick={() => book(null)}
              disabled={!walkName.trim() || create.isPending}
              className="w-full h-10 rounded-xl bg-teal-600 text-white text-[13px] font-semibold hover:bg-teal-700 transition-colors disabled:opacity-40"
            >
              {create.isPending ? "Booking…" : "Book walk-in"}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── An existing booking — details, reschedule, cancel ────── */

function AppointmentDialog({
  appt,
  freeSlots,
  onClose,
}: {
  appt: ShopAppointment
  freeSlots: SlotInfo[]
  onClose: () => void
}) {
  const reschedule = useRescheduleAppointment()
  const cancel = useCancelAppointment()
  const setOutcome = useSetAppointmentOutcome()
  const navigate = useNavigate()
  const [moving, setMoving] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const when = new Date(appt.startsAt).toLocaleString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
  // Once the slot has passed the question changes: not "move it?" but
  // "did they show?" — that answer drives what happens next.
  const isPast = new Date(appt.startsAt).getTime() < Date.now()

  const recordOutcome = (outcome: "attended" | "no_show") =>
    setOutcome.mutate(
      { id: appt.id, outcome },
      {
        onSuccess: () => {
          toast.success(
            outcome === "attended"
              ? `${appt.customerName.split(" ")[0]} came in — noted`
              : "No-show — follow-up resumed and their booking link re-opened",
          )
          onClose()
        },
        onError: () => toast.error("Couldn't save. Try again."),
      },
    )

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-zinc-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Appointment"
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[16px] font-bold text-zinc-900 truncate">{appt.customerName}</p>
            <p className="text-[12.5px] text-zinc-500 mt-0.5">
              {when} · {KIND_LABEL[appt.kind]}
            </p>
            <p className="text-[12px] text-zinc-400 mt-0.5 truncate">
              {appt.vehicleLabel ?? "Vehicle"}
              {appt.customerPhone && <> · {appt.customerPhone}</>}
              {appt.estimateValue ? <> · {formatMoney(appt.estimateValue)}</> : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 shrink-0"
          >
            <XClose className="size-4" aria-hidden />
          </button>
        </div>

        {/* Past visit, unanswered — the one question that matters. */}
        {isPast && appt.outcome == null && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-3.5">
            <p className="text-[13px] font-semibold text-amber-900">Did they show?</p>
            <div className="mt-2.5 flex items-center gap-2">
              <button
                type="button"
                disabled={setOutcome.isPending}
                onClick={() => recordOutcome("attended")}
                className="h-9 px-3.5 rounded-xl bg-emerald-600 text-white text-[12.5px] font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                ✓ They came
              </button>
              <button
                type="button"
                disabled={setOutcome.isPending}
                onClick={() => recordOutcome("no_show")}
                className="h-9 px-3.5 rounded-xl border border-amber-300 bg-white text-[12.5px] font-semibold text-amber-800 hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                No-show
              </button>
            </div>
            <p className="mt-2 text-[11.5px] text-amber-800/80 leading-snug">
              A no-show puts the estimate back in follow-up and re-opens their
              booking link so they can pick a new time.
            </p>
          </div>
        )}
        {appt.outcome === "no_show" && (
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-2.5 py-1.5 text-[12px] font-semibold text-zinc-500">
            No-show — follow-up resumed
          </p>
        )}
        {appt.outcome === "attended" && (
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-[12px] font-semibold text-emerald-700">
            ✓ Came in
          </p>
        )}

        {!moving ? (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {appt.estimateId != null && (
              <button
                type="button"
                onClick={() => {
                  onClose()
                  navigate(`/estimates/${appt.estimateId}`)
                }}
                className="h-9 px-3 rounded-xl bg-zinc-900 text-white text-[12.5px] font-semibold hover:bg-black transition-colors"
              >
                Open estimate
              </button>
            )}
            {!isPast && (
            <button
              type="button"
              onClick={() => setMoving(true)}
              className="h-9 px-3 rounded-xl border border-zinc-200 text-[12.5px] font-semibold text-zinc-700 hover:border-zinc-300 transition-colors"
            >
              Reschedule
            </button>
            )}
            {/* Cancelling a visit that already passed makes no sense — the
                did-they-show answer above is the past's only action. */}
            {isPast ? null : !confirmCancel ? (
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                className="h-9 px-3 rounded-xl text-[12.5px] font-semibold text-red-600 hover:bg-red-50 transition-colors"
              >
                Cancel booking
              </button>
            ) : (
              <button
                type="button"
                disabled={cancel.isPending}
                onClick={() =>
                  cancel.mutate(appt.id, {
                    onSuccess: () => {
                      toast.success("Booking cancelled")
                      onClose()
                    },
                    onError: () => toast.error("Couldn't cancel."),
                  })
                }
                className="h-9 px-3 rounded-xl bg-red-600 text-white text-[12.5px] font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancel.isPending ? "Cancelling…" : "Yes, cancel it"}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">
              Pick a new time
            </p>
            <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
              {freeSlots.slice(0, 24).map((s) => (
                <button
                  key={s.startsAt}
                  type="button"
                  disabled={reschedule.isPending}
                  onClick={() =>
                    reschedule.mutate(
                      { id: appt.id, startsAt: s.startsAt },
                      {
                        onSuccess: () => {
                          toast.success("Rescheduled")
                          onClose()
                        },
                        onError: () => toast.error("Couldn't reschedule."),
                      },
                    )
                  }
                  className="h-9 rounded-lg border border-zinc-200 text-[11.5px] font-semibold text-zinc-700 hover:border-teal-300 hover:bg-teal-50/50 transition-colors disabled:opacity-50"
                >
                  {new Date(s.startsAt).toLocaleString("en-US", {
                    weekday: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMoving(false)}
              className="mt-2 h-9 px-3 rounded-xl text-[12.5px] font-semibold text-zinc-500 hover:bg-zinc-50"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
